import { toE164 } from 'shared/helpers/Validators';
import { formatAddressForMaps } from 'shared/helpers/addressFormatter';

/**
 * Deep-link builders for maps, WhatsApp and phone dialing
 * (see reference doc 03 §3.1 and §5.3).
 */

const WAZE_BASE_URL = 'https://waze.com/ul';
const GOOGLE_MAPS_BASE_URL = 'https://www.google.com/maps/search/?api=1';
const WHATSAPP_BASE_URL = 'https://wa.me';

/**
 * Builds a Waze navigation deep-link to a coordinate.
 * @param {{ lat: (number|string), lng: (number|string) }} coordinates
 * @returns {string|null} The Waze URL, or null when coordinates are missing.
 */
export const wazeLink = ({ lat, lng } = {}) => {
  if (lat === undefined || lat === null || lng === undefined || lng === null) {
    return null;
  }
  return encodeURI(`${WAZE_BASE_URL}?ll=${lat},${lng}&navigate=yes`);
};

/**
 * Builds a Google Maps search deep-link from a structured address.
 * @param {object} address - Structured address object.
 * @returns {string|null} The Google Maps URL, or null when the address is empty.
 */
export const googleMapsLink = address => {
  const query = formatAddressForMaps(address);
  if (!query) return null;
  return `${GOOGLE_MAPS_BASE_URL}&query=${encodeURIComponent(query)}`;
};

/**
 * Builds a WhatsApp deep-link to a phone number, normalized to E.164 digits.
 * @param {string} phone - The phone number (BR-normalized via libphonenumber).
 * @param {string} [text] - Optional prefilled message.
 * @returns {string|null} The wa.me URL, or null when no phone is given.
 */
export const buildWhatsappLink = (phone, text = '') => {
  if (!phone) return null;

  const digits = toE164(phone).replace(/\D/g, '');
  if (!digits) return null;

  const url = `${WHATSAPP_BASE_URL}/${digits}`;
  return text ? `${url}?text=${encodeURIComponent(text)}` : url;
};

/**
 * Builds a `tel:` link to a phone number, normalized to E.164.
 * @param {string} phone - The phone number (BR-normalized via libphonenumber).
 * @returns {string|null} The tel: URI, or null when no phone is given.
 */
export const buildTelLink = phone => {
  if (!phone) return null;
  return `tel:${toE164(phone)}`;
};
